"use client";

import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/utils";
import {
  QuantityAddToCart,
  type ProductCardVariant,
} from "@/components/blocks/QuantityAddToCart";

export type { ProductCardVariant };

interface ProductCardExtraBadge {
  label: string;
  tone?: "brand" | "destructive";
}

interface ProductCardProps {
  name: string;
  description?: string;
  category?: string;
  extraBadge?: ProductCardExtraBadge;
  imageUrl?: string;
  priceLabel: string;
  unitLabel?: string;
  locationLabel?: string;
  /** Pending quantity to add, used while the product isn't in the cart yet. */
  quantity: number;
  maxQuantity?: number;
  onQuantityChange?: (value: string) => void;
  onAddToCart?: () => void;
  isPending?: boolean;
  /** Renders the full quantity picker (-/input/+) before adding to cart.
   * When false, a compact Add to Cart button is shown instead — the picker
   * still appears automatically once the product is in the cart. */
  showQuantity?: boolean;
  addToCartLabel?: string;
  /** Current quantity of this product already in the cart. When > 0 (and
   * onCartQuantityChange is provided), +/- and the number input adjust the
   * cart directly instead of the local pending quantity. */
  cartQuantity?: number;
  onCartQuantityChange?: (quantity: number) => void;
  isCartUpdating?: boolean;
  /** "default" matches the app's internal dashboard layout; "storefront" is the branded,
   * emerald-accented look used on customer-facing store & marketplace pages. Both variants
   * use theme tokens and respond to the light/dark toggle. */
  variant?: ProductCardVariant;
  className?: string;
}

/**
 * Canonical product card — consolidates MarketplaceProductCard, store-detail-product-card,
 * and PublicProductCard into one presentational component. `variant="storefront"` preserves
 * the deliberately distinct customer-facing branding (white card, emerald accents) those two
 * used, rather than forcing the app's internal dark theme onto public pages.
 */
export function ProductCard({
  name,
  description,
  category,
  extraBadge,
  imageUrl,
  priceLabel,
  unitLabel,
  locationLabel,
  quantity,
  maxQuantity,
  onQuantityChange,
  onAddToCart,
  isPending,
  showQuantity = true,
  addToCartLabel = "Add to Cart",
  cartQuantity,
  onCartQuantityChange,
  isCartUpdating,
  variant = "default",
  className,
}: ProductCardProps) {
  const inCart = (cartQuantity ?? 0) > 0 && !!onCartQuantityChange;
  const showPicker = showQuantity || inCart;

  const quantityControl = (
    <QuantityAddToCart
      name={name}
      quantity={quantity}
      maxQuantity={maxQuantity}
      onQuantityChange={onQuantityChange}
      onAddToCart={onAddToCart}
      isPending={isPending}
      addToCartLabel={addToCartLabel}
      cartQuantity={cartQuantity}
      onCartQuantityChange={onCartQuantityChange}
      isCartUpdating={isCartUpdating}
      variant={variant}
    />
  );

  if (variant === "storefront") {
    return (
      <Card
        className={cn(
          "group overflow-hidden border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg",
          className,
        )}
      >
        <div className="relative aspect-[4/3] bg-gradient-to-br from-primary/10 to-muted">
          {imageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={imageUrl}
              alt={name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="h-24 w-24 rounded-full bg-primary/20 blur-2xl" />
            </div>
          )}

          {category || extraBadge ? (
            <div className="absolute left-3 top-3 flex flex-col gap-2">
              {category ? (
                <Badge variant="brand" className="w-fit rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em]">
                  {category}
                </Badge>
              ) : null}
              {extraBadge ? (
                <Badge
                  variant={
                    extraBadge.tone === "destructive"
                      ? "destructive"
                      : "default"
                  }
                  className="w-fit rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em]"
                >
                  {extraBadge.label}
                </Badge>
              ) : null}
            </div>
          ) : null}
        </div>

        <CardContent className="space-y-4 p-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold leading-tight text-foreground">
              {name}
            </h3>
            {description ? (
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {description}
              </p>
            ) : null}
            {locationLabel ? (
              <p className="text-xs text-muted-foreground">{locationLabel}</p>
            ) : null}
          </div>

          <div className="text-xl font-bold text-emerald-600">
            {priceLabel}
            {unitLabel ? (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {unitLabel}
              </span>
            ) : null}
          </div>

          {showPicker ? (
            quantityControl
          ) : (
            <Button
              type="button"
              className="h-11 w-full rounded-xl bg-card text-foreground shadow-sm transition hover:bg-primary/10"
              variant="outline"
              onClick={onAddToCart}
              disabled={!onAddToCart || isPending || isCartUpdating}
            >
              {isPending || isCartUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {isPending || isCartUpdating ? "Adding..." : addToCartLabel}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("overflow-hidden border-white/10", className)}>
      <div className="aspect-[4/3] bg-muted/30">
        {imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={imageUrl}
            alt={name}
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
      <CardContent className="space-y-4 p-4">
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-semibold">{name}</h3>
            {category ? <Badge variant="secondary">{category}</Badge> : null}
          </div>
          {description ? (
            <p className="line-clamp-2 text-sm text-muted-foreground">
              {description}
            </p>
          ) : null}
          {locationLabel ? (
            <p className="text-sm text-muted-foreground">{locationLabel}</p>
          ) : null}
          <p className="text-base font-semibold">
            {priceLabel}
            {unitLabel ? ` · ${unitLabel}` : null}
          </p>
        </div>

        {showPicker ? (
          quantityControl
        ) : (
          <Button
            type="button"
            className="w-full"
            onClick={onAddToCart}
            disabled={isPending || isCartUpdating}
          >
            {isPending || isCartUpdating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            {isPending || isCartUpdating ? "Adding..." : addToCartLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
